import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

import { Order } from 'order/interfaces/Order';
import { setOrderPoNumber, updateOrder } from 'order/store/orderActions';

import { ConfirmationModal } from 'shared/components/ConfirmationModal';
import { Input } from 'shared/components/Input';
import { Spacer } from 'shared/components/Layout';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';

const buildOrderPayload = (order: Order, postOfficeNumber: string) =>
  ({
    id: order.id,
    jobId: order.job?.id,
    postOfficeNumber,
    name: order.name,
    note: order.note,
    ceilingHeight: order.ceilingHeight,
    countertopThickness: order.countertopThickness,
    dateRequested: order.dateRequested,
    depositPaid: order.depositPaid,
    depositPaidDate: order.depositPaidDate ?? null,
    descriptionType: order.descriptionType,
    jobSiteDelivery: order.jobSiteDelivery,
    stylizationType: order.stylizationType,
    transportationType: order.transportationType,
    type: order.type,
    workOrderNumber: order.workOrderNumber,
    adjustment: order.adjustment,
  } as Order);

interface PoRequiredModalProps {
  buttonText?: string;
}

export const usePoRequired = () => {
  const dispatch = useAppDispatch();

  const order = useSelector((state: RootState) => state.orderReducer.order);
  const dealership = useSelector(
    (state: RootState) => state.dealershipReducer.dealership
  );

  const [opened, setOpened] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [onProceed, setOnProceed] = useState<(() => void) | null>(null);

  const close = () => {
    setOpened(false);
    setInputValue('');
  };

  const handleConfirm = () => {
    const trimmedPo = inputValue.trim();

    if (trimmedPo && order) {
      setSaving(true);

      dispatch(
        updateOrder(
          buildOrderPayload(order, trimmedPo),
          () => {
            setSaving(false);
            close();
            dispatch(setOrderPoNumber(trimmedPo));
            toast.success('P.O Number saved successfully on order.');
            onProceed?.();
          },
          () => {
            setSaving(false);
            close();
            toast.warn(
              'P.O Number save failed! You can try to set it up again on the order Basic Info page.'
            );
            onProceed?.();
          }
        )
      );
    } else {
      close();
      onProceed?.();
    }
  };

  const guardWithPoCheck = (proceed: () => void) => {
    if (dealership?.poRequired && !order?.postOfficeNumber?.trim()) {
      setOnProceed(() => proceed);
      setOpened(true);
      return;
    }

    proceed();
  };

  const PoRequiredModal: FC<PoRequiredModalProps> = ({
    buttonText = 'Continue',
  }) => (
    <ConfirmationModal
      opened={opened}
      title="P.O Required"
      message="This dealership requires a P.O, please enter one."
      confirm={handleConfirm}
      buttonText={buttonText}
      cancelButtonText="Cancel"
      cancel={close}
      buttonDisabled={saving}
      disableLoading
      bottomSpacing={30}
    >
      <Spacer h="20px" />
      <Input
        placeholder="P.O Number"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
      />
    </ConfirmationModal>
  );

  return { guardWithPoCheck, PoRequiredModal };
};
